import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import axios from 'axios'
import useGlobalState, { g_user, API_Root } from "../../globalState";
import { renderTripRoute } from "../trip/tripRoute";
import { vehicleRole, vehicleTypeLabel } from "../trip/vehicleRole";
import { vehicleName } from "../trip/vehicleName";

const vehicleLabel = (s) => vehicleName(s, 'Unspecified vehicle');

export const AvailableTrip = ({ trip, onChange, onClaimed, onClaimFailed, onPrint }) => {
   const [user,] = useGlobalState(g_user);
   const [roundTrips, setRoundTrips] = useState([]);
   const [returns, setReturns] = useState([]);
   const [deliveries, setDeliveries] = useState([]);
   const [allChase, setAllChase] = useState([]);
   const [allReturns, setAllReturns] = useState([]);
   const [allDeliveries, setAllDeliveries] = useState([]);
   const [showSelectReturn, setShowSelectReturn] = useState(false);
   const [claimingId, setClaimingId] = useState(null);
   const [vehicleToClaim, setVehicleToClaim] = useState(null);
   const [claimTypeLabel, setClaimTypeLabel] = useState('');
   const claimingRef = useRef(false);

   useEffect(() => {
      let round = [];
      let there = [];
      let back = [];
      let chase = [];
      let allThere = [];
      let allBack = [];

      if (trip) {
         trip.TripVehicles.forEach(veh => {
            const role = vehicleRole(veh, trip.TripLocations);
            if (role === 'roundTrip') {
               chase.push(veh);
            } else if (role === 'delivery') {
               allThere.push(veh);
            } else {
               allBack.push(veh);
            }

            if (!veh.User) {
               if (role === 'roundTrip') {
                  round.push(veh);
               } else if (role === 'delivery') {
                  there.push(veh);
               } else {
                  back.push(veh);
               }
            }
         });
         setRoundTrips(round);
         setReturns(back);
         setDeliveries(there);
         setAllChase(chase);
         setAllDeliveries(allThere);
         setAllReturns(allBack);
      }
   }, [trip]);

   const finishClaiming = () => {
      claimingRef.current = false;
      setClaimingId(null);
   }

   const confirmClaimTrip = (veh, typeLabel) => {
      if (claimingRef.current) {
         return;
      }
      setVehicleToClaim(veh);
      setClaimTypeLabel(typeLabel || '');
   }

   const closeClaimDialog = () => {
      if (!claimingRef.current) {
         setVehicleToClaim(null);
         setClaimTypeLabel('');
      }
   }

   const claimTrip = (veh) => {
      if (!veh || claimingRef.current) {
         return;
      }
      claimingRef.current = true;
      setClaimingId(veh.id);
      var apiUrl = `${API_Root}api/trip/AssignPorterToVehicle?tripId=${trip.id}&porterId=${user.user.id}&vehicleId=${veh.id}`;
      axios.get(apiUrl).then((values) => {
         if (values.data !== true) {
            finishClaiming();
            if (onClaimFailed) {
               onClaimFailed();
            }
            onChange();
            return;
         }
         if (onClaimed) {
            onClaimed(vehicleLabel(veh), trip.id);
         }

         const remainingReturns = returns.filter((r) => r.id !== veh.id);
         const claimedOutbound = deliveries.some((d) => d.id === veh.id) || roundTrips.some((r) => r.id === veh.id);

         setDeliveries((prev) => prev.filter((v) => v.id !== veh.id));
         setRoundTrips((prev) => prev.filter((v) => v.id !== veh.id));
         setReturns(remainingReturns);

         if (claimedOutbound && remainingReturns.length > 0) {
            setVehicleToClaim(null);
            setClaimTypeLabel('');
            setShowSelectReturn(true);
            finishClaiming();
         } else {
            setShowSelectReturn(false);
            Promise.resolve(onChange()).finally(() => {
               setVehicleToClaim(null);
               setClaimTypeLabel('');
               finishClaiming();
            });
         }
      }).catch(() => {
         finishClaiming();
         if (onClaimFailed) {
            onClaimFailed();
         }
      });
   }

   const skipReturn = () => {
      if (claimingRef.current || vehicleToClaim) {
         return;
      }
      setShowSelectReturn(false);
      onChange();
   }

   const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
   });

   const estimatedFee = () => {
      const settings = trip.Dealership?.DealershipSettings?.[0];
      const minFee = settings?.minFee ?? 0;
      const applyMin = (amount) => (minFee > 0 && amount < minFee ? minFee : amount);
      if (trip.MileageCost > 0) {
         return formatter.format(applyMin(trip.MileageCost));
      }
      const hourly = (settings?.rateMethod ?? '').toLowerCase() === 'h';
      const rate = settings?.rate > 0 ? settings.rate : (hourly ? 15 : 0.35);
      const raw = hourly
         ? (trip.estimatedHours / 60) * rate
         : trip.estimatedMiles * rate;
      return formatter.format(applyMin(raw));
   };

   const formatWindow = () => {
      const start = new Date(trip.requestedDeliveryDateTime);
      const startText = start.toLocaleDateString('en-US', {
         weekday: 'short',
         month: 'short',
         day: 'numeric',
         hour: 'numeric',
         minute: '2-digit',
         hour12: true
      });
      if (trip.requestedDeliveryDateTimeEnd) {
         const end = new Date(trip.requestedDeliveryDateTimeEnd);
         const endText = end.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
         });
         return `${startText} – ${endText}`;
      }
      return startText;
   };

   const vehicleRow = (s, typeLabel) => {
      return (
         <div key={s.id} className="job-vehicle-row">
            <div className="job-vehicle-info">
               <span className="job-vehicle-type">{typeLabel}</span>
               <span className="job-vehicle-name">{vehicleLabel(s)}</span>
               <div className="job-card-route">{renderTripRoute(trip, [s])}</div>
            </div>
            <Button
               label="Claim"
               icon="pi pi-check"
               size="small"
               disabled={!!claimingId || !!vehicleToClaim}
               loading={claimingId === s.id}
               onClick={() => confirmClaimTrip(s, typeLabel)}
            />
         </div>
      );
   }

   const claimableVehicles = () => {
      if (deliveries.length > 0 || roundTrips.length > 0 || trip.userHasDriveThere) {
         return (
            <>
               {deliveries.map((s) => vehicleRow(s, vehicleTypeLabel(s, trip.TripLocations)))}
               {roundTrips.map((s) => vehicleRow(s, vehicleTypeLabel(s, trip.TripLocations)))}
            </>
         );
      }
      return returns.map((s) => vehicleRow(s, vehicleTypeLabel(s, trip.TripLocations)));
   }

   const getReturnInfo = () => {
      if (roundTrips.length > 0 || deliveries.length > 0) {
         if (allReturns.length > 0) {
            return 'After delivery, a return vehicle is available.';
         }
         if (allChase.length > 0) {
            return 'A follow vehicle is available to get you back.';
         }
         return 'You may need another way back after delivery.';
      }
      if (returns.length > 0) {
         return 'These are return vehicles for the drive back.';
      }
      return '';
   }

   return (
      <>
         <div className="job-card">
            <div className="job-card-top">
               <div>
                  <div className="job-card-when">Deliver by {formatWindow()}</div>
                  <div className="job-card-route">{renderTripRoute(trip, trip.TripVehicles)}</div>
                  <div className="job-card-meta">
                     {trip.estimatedDistance}{trip.estimatedDuration ? ` · ${trip.estimatedDuration}` : ''}
                     {` · ${trip.TripVehicles?.length || 0} vehicle${(trip.TripVehicles?.length || 0) !== 1 ? 's' : ''}`}
                  </div>
               </div>
               <div className="job-card-pay">
                  <div className="job-card-fee">{estimatedFee()}</div>
                  <Button label="Print trip sheet" icon="pi pi-print" text size="small" onClick={() => onPrint()} />
               </div>
            </div>
            <div className="job-card-vehicles">
               <div className="job-card-section">Need a driver</div>
               {claimableVehicles()}
               {getReturnInfo() && <p className="job-card-hint">{getReturnInfo()}</p>}
            </div>
         </div>
         <Dialog
            className="claim-dialog"
            header="Claim this order?"
            visible={!!vehicleToClaim}
            style={{ width: '28rem' }}
            breakpoints={{ '641px': '95vw' }}
            closable={!claimingId}
            closeOnEscape={!claimingId}
            onHide={() => closeClaimDialog()}
            footer={(
               <div className="claim-dialog-footer">
                  <Button
                     label="Cancel"
                     className="p-button-text"
                     onClick={() => closeClaimDialog()}
                     disabled={!!claimingId}
                  />
                  <Button
                     label="Claim order"
                     icon="pi pi-check"
                     onClick={() => claimTrip(vehicleToClaim)}
                     loading={!!claimingId}
                     disabled={!!claimingId}
                  />
               </div>
            )}>
            {vehicleToClaim && (
               <div className="claim-dialog-body">
                  {claimTypeLabel && <div className="job-vehicle-type">{claimTypeLabel}</div>}
                  <div className="claim-dialog-vehicle">{vehicleLabel(vehicleToClaim)}</div>
                  <div className="claim-dialog-recap">
                     <div className="job-card-route">{renderTripRoute(trip, [vehicleToClaim])}</div>
                     <div className="job-card-meta">Deliver by {formatWindow()}</div>
                  </div>
                  <p className="job-card-hint">You’re taking this vehicle. You can still print the trip sheet after you claim.</p>
               </div>
            )}
         </Dialog>
         <Dialog
            header="Choose a vehicle for the drive back"
            visible={showSelectReturn}
            style={{ width: '28rem' }}
            breakpoints={{ '641px': '95vw' }}
            closable={!claimingId && !vehicleToClaim}
            closeOnEscape={!claimingId && !vehicleToClaim}
            onHide={() => skipReturn()}>
            <p className="job-card-hint mt-0">Optional — claim a return vehicle now, or skip and do it later.</p>
            <div className="job-card-vehicles">
               {returns.map((s) => vehicleRow(s, vehicleTypeLabel(s, trip.TripLocations)))}
            </div>
            <div className="text-right mt-3">
               <Button
                  label="Skip for now"
                  className="p-button-text"
                  onClick={() => skipReturn()}
                  disabled={!!claimingId || !!vehicleToClaim}
               />
            </div>
         </Dialog>
      </>
   );
}
